class H2OAiProductSupport extends HTMLElement {
  constructor() {
    super();
    this.dialog = this.querySelector('[data-ai-dialog]');
    this.openButton = this.querySelector('[data-ai-open]');
    this.closeButton = this.querySelector('[data-ai-close]');
    this.form = this.querySelector('[data-ai-form]');
    this.input = this.querySelector('[data-ai-input]');
    this.sendButton = this.querySelector('[data-ai-send]');
    this.messages = this.querySelector('[data-ai-messages]');
    this.suggestions = this.querySelector('[data-ai-suggestions]');
    this.productSummary = this.querySelector('[data-ai-product-summary]');
    this.modeButtons = [...this.querySelectorAll('[data-ai-mode]')];
    this.mode = 'technical';
    this.history = [];
    this.sessionId = self.crypto?.randomUUID?.() || Math.random().toString(36).slice(2);
    this.context = this.readContext();
    this.lastFocused = null;
    this.abortController = null;
    this.scrollLockState = null;
    this.visualViewportFrame = null;

    if (!this.dialog || !this.context?.product || !this.dataset.endpoint) return;

    this.onDocumentChange = this.onDocumentChange.bind(this);
    this.onDialogClick = this.onDialogClick.bind(this);
    this.onDialogClose = this.onDialogClose.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.syncVisualViewport = this.syncVisualViewport.bind(this);

    this.openButton?.addEventListener('click', () => this.open());
    this.closeButton?.addEventListener('click', () => this.close());
    this.dialog.addEventListener('click', this.onDialogClick);
    this.dialog.addEventListener('close', this.onDialogClose);
    this.dialog.addEventListener('keydown', this.onKeydown);
    this.form?.addEventListener('submit', (event) => this.submit(event));
    this.modeButtons.forEach((button) => button.addEventListener('click', () => this.setMode(button.dataset.aiMode)));
    this.querySelectorAll('[data-ai-escalate]').forEach((link) => {
      link.addEventListener('click', () => this.track('h2o_ai_support_escalation_clicked'));
    });
    document.addEventListener('change', this.onDocumentChange);

    this.updateSelectedVariant();
    this.renderSuggestions();
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.onDocumentChange);
    this.abortController?.abort();
    this.stopVisualViewportSync();
    this.unlockPageScroll();
  }

  readContext() {
    try {
      return JSON.parse(this.querySelector('[data-ai-product-context]')?.textContent || '{}');
    } catch (error) {
      return null;
    }
  }

  get selectedVariant() {
    const variants = this.context?.product?.variants || [];
    return variants.find((variant) => String(variant.id) === String(this.context.product.selectedVariantId)) || variants[0] || null;
  }

  onDocumentChange(event) {
    if (event.target?.matches('input[name="id"], variant-radios input, variant-selects select')) {
      requestAnimationFrame(() => this.updateSelectedVariant());
    }
  }

  updateSelectedVariant() {
    const sectionId = this.dataset.sectionId;
    const variantInput = document.querySelector(
      '#product-form-' + CSS.escape(sectionId) + ' input[name="id"], #ProductInfo-' + CSS.escape(sectionId) + ' input[name="id"]'
    );
    if (variantInput?.value) this.context.product.selectedVariantId = Number(variantInput.value);
    this.renderProductSummary();
  }

  getLiveFulfilmentContext() {
    const root = document.getElementById('MainProduct-' + this.dataset.sectionId) || this.closest('[id^="MainProduct-"]');
    const delivery = root?.querySelector('[data-delivery-pill-text]')?.textContent?.trim() || '';
    const pickup = root?.querySelector('pickup-availability')?.textContent?.replace(/\s+/g, ' ').trim() || '';
    return {
      delivery: delivery.slice(0, 300),
      pickup: pickup.slice(0, 500)
    };
  }

  renderProductSummary() {
    if (!this.productSummary) return;
    const variant = this.selectedVariant;
    this.productSummary.textContent = '';
    const label = document.createElement('strong');
    label.textContent = this.context.product.title;
    this.productSummary.append(label);
    if (variant && variant.title !== 'Default Title') {
      this.productSummary.append(document.createTextNode(' · ' + variant.title));
    }
  }

  get isMobileLayout() {
    return this.dataset.templateSuffix === 'h2o-standard' && window.matchMedia('(max-width: 749px)').matches;
  }

  lockPageScroll() {
    if (this.scrollLockState) return;

    const body = document.body;
    const scrollY = window.scrollY;
    const fixBody = this.isMobileLayout;
    this.scrollLockState = {
      fixBody,
      scrollY,
      styles: {
        position: body.style.position,
        top: body.style.top,
        left: body.style.left,
        right: body.style.right,
        width: body.style.width,
        overflow: body.style.overflow,
        overscrollBehavior: body.style.overscrollBehavior
      }
    };

    document.documentElement.classList.add('h2o-ai-support-open');
    body.classList.add('h2o-ai-support-open');

    if (fixBody) {
      body.style.position = 'fixed';
      body.style.top = '-' + scrollY + 'px';
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
      body.style.overflow = 'hidden';
      body.style.overscrollBehavior = 'none';
    }
  }

  unlockPageScroll() {
    if (!this.scrollLockState) return;

    const body = document.body;
    const { fixBody, scrollY, styles } = this.scrollLockState;
    document.documentElement.classList.remove('h2o-ai-support-open');
    body.classList.remove('h2o-ai-support-open');

    Object.entries(styles).forEach(([property, value]) => {
      body.style[property] = value;
    });

    this.scrollLockState = null;
    if (fixBody) window.scrollTo(0, scrollY);
  }

  startVisualViewportSync() {
    if (!this.isMobileLayout || !window.visualViewport) return;

    window.visualViewport.addEventListener('resize', this.syncVisualViewport);
    window.visualViewport.addEventListener('scroll', this.syncVisualViewport);
    this.syncVisualViewport();
  }

  stopVisualViewportSync() {
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.syncVisualViewport);
      window.visualViewport.removeEventListener('scroll', this.syncVisualViewport);
    }
    if (this.visualViewportFrame) cancelAnimationFrame(this.visualViewportFrame);
    this.visualViewportFrame = null;
    this.dialog?.style.removeProperty('--h2o-ai-viewport-height');
    this.dialog?.style.removeProperty('--h2o-ai-viewport-top');
  }

  syncVisualViewport() {
    if (!this.dialog?.open || !this.isMobileLayout || !window.visualViewport) return;

    if (this.visualViewportFrame) cancelAnimationFrame(this.visualViewportFrame);
    this.visualViewportFrame = requestAnimationFrame(() => {
      this.dialog.style.setProperty('--h2o-ai-viewport-height', Math.round(window.visualViewport.height) + 'px');
      this.dialog.style.setProperty('--h2o-ai-viewport-top', Math.round(window.visualViewport.offsetTop) + 'px');
      this.visualViewportFrame = null;
    });
  }

  open() {
    this.lastFocused = document.activeElement;
    this.updateSelectedVariant();
    if (!this.messages.childElementCount) {
      this.addMessage('assistant', 'Ask me about ' + this.context.product.title + '. I’ll use the approved product information available on this page and flag anything I cannot verify.');
    }
    this.dialog.showModal();
    this.lockPageScroll();
    this.startVisualViewportSync();
    if (this.isMobileLayout) {
      this.closeButton?.focus({ preventScroll: true });
    } else {
      this.input?.focus();
    }
    this.track('h2o_ai_support_opened');
  }

  close() {
    this.dialog.close();
  }

  onDialogClick(event) {
    if (event.target === this.dialog) this.close();
  }

  onDialogClose() {
    this.stopVisualViewportSync();
    this.unlockPageScroll();
    this.lastFocused?.focus?.({ preventScroll: true });
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }

  setMode(mode) {
    if (!['technical', 'ordering'].includes(mode)) return;
    this.mode = mode;
    this.modeButtons.forEach((button) => {
      const active = button.dataset.aiMode === mode;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-checked', String(active));
    });
    this.renderSuggestions();
    this.track(mode === 'technical' ? 'h2o_ai_support_technical_selected' : 'h2o_ai_support_ordering_selected');
  }

  getSuggestions() {
    if (this.mode === 'ordering') {
      return [
        'Which pack size am I viewing?',
        'Is this variant available to order?',
        'What complementary products should I consider?',
        'What are the dispatch and Click & Collect options?'
      ];
    }

    const groups = {
      primer: [
        'What substrates is this suitable for?',
        'What is the mixing ratio?',
        'What can I apply over this primer?',
        'How much product will I need?'
      ],
      membrane: [
        'What primer should I use?',
        'How many coats are required?',
        'What substrates is this suitable for?',
        'When can it be tiled or backfilled?'
      ],
      sealant: [
        'What substrates can this bond to?',
        'Does it require a primer?',
        'What joint sizes are suitable?',
        'Can it be used with a waterproofing membrane?'
      ],
      adhesive: [
        'Which substrates is this suitable for?',
        'What is the coverage rate?',
        'What is the open and curing time?',
        'Which primer or preparation is required?'
      ],
      general: [
        'What is this product used for?',
        'What substrates is it suitable for?',
        'How is it applied?',
        'What limitations should I know about?'
      ]
    };
    return groups[this.dataset.suggestionGroup] || groups.general;
  }

  renderSuggestions() {
    if (!this.suggestions) return;
    this.suggestions.textContent = '';
    this.getSuggestions().forEach((question) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'h2o-ai-support__suggestion';
      button.textContent = question;
      button.addEventListener('click', () => {
        this.input.value = question;
        this.input.focus();
        this.track('h2o_ai_support_suggested_question_clicked');
      });
      this.suggestions.append(button);
    });
  }

  async submit(event) {
    event.preventDefault();
    const question = this.input.value.trim().slice(0, 800);
    if (!question || this.sendButton.disabled) return;

    this.updateSelectedVariant();
    this.addMessage('user', question);
    this.history.push({ role: 'user', content: question });
    this.input.value = '';
    this.setLoading(true);
    this.track('h2o_ai_support_question_submitted');

    this.abortController?.abort();
    this.abortController = new AbortController();
    const timeout = window.setTimeout(() => this.abortController.abort(), 25000);

    try {
      const response = await fetch(this.dataset.endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          version: 1,
          sessionId: this.sessionId,
          mode: this.mode,
          question,
          history: this.history.slice(-6),
          product: {
            ...this.context.product,
            variants: undefined,
            selectedVariant: this.selectedVariant,
            fulfilment: this.getLiveFulfilmentContext()
          }
        }),
        signal: this.abortController.signal
      });

      const contentType = response.headers.get('content-type') || '';
      if (!response.ok || !contentType.includes('application/json')) throw new Error('AI endpoint unavailable');

      const data = await response.json();
      const answer = typeof data.answer === 'string' ? data.answer.trim().slice(0, 6000) : '';
      if (!answer) throw new Error('Empty AI response');

      this.addMessage('assistant', answer, Array.isArray(data.sources) ? data.sources.slice(0, 5) : []);
      this.history.push({ role: 'assistant', content: answer });
    } catch (error) {
      if (error.name !== 'AbortError' || this.dialog.open) {
        this.addMessage(
          'error',
          'AI Product Support is temporarily unavailable. Please contact H2O Technical Support for assistance.'
        );
      }
    } finally {
      window.clearTimeout(timeout);
      this.setLoading(false);
      this.input?.focus();
    }
  }

  setLoading(loading) {
    this.sendButton.disabled = loading;
    this.input.disabled = loading;
    this.sendButton.querySelector('span')?.replaceChildren(document.createTextNode(loading ? 'Checking…' : 'Send'));
  }

  addMessage(role, text, sources = []) {
    const message = document.createElement('div');
    const type = role === 'error' ? 'assistant h2o-ai-support__message--error' : role;
    message.className = 'h2o-ai-support__message h2o-ai-support__message--' + type;
    message.textContent = text;

    const validSources = sources.filter((source) => {
      try {
        const url = new URL(source?.url, window.location.origin);
        return ['https:', 'http:'].includes(url.protocol) && typeof source?.label === 'string';
      } catch (error) {
        return false;
      }
    });

    if (validSources.length) {
      const sourceList = document.createElement('div');
      sourceList.className = 'h2o-ai-support__message-sources';
      validSources.forEach((source) => {
        const link = document.createElement('a');
        link.href = source.url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.textContent = source.label.slice(0, 100);
        sourceList.append(link);
      });
      message.append(sourceList);
    }

    this.messages.append(message);
    this.messages.scrollTop = this.messages.scrollHeight;
  }

  track(eventName) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      product_id: String(this.context.product.id),
      variant_id: String(this.selectedVariant?.id || ''),
      ai_support_mode: this.mode
    });
  }
}

if (!customElements.get('h2o-ai-product-support')) {
  customElements.define('h2o-ai-product-support', H2OAiProductSupport);
}
